import { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { useReveal } from "@/hooks/useReveal";
import { toast } from "sonner";
import { openCalendly } from "@/lib/calendly";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ALL_SERVICES = [
  "Meta Ads",
  "Google Ads",
  "TikTok Ads",
  "Organic Marketing",
  "Social Media Management",
  "Hjemmeside",
  "SEO Optimization",
  "Video Redigering",
  "UGC Content",
];

export const Contact = () => {
  const ref = useReveal();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    services: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // useReveal hook uses .in-view class which works on both .reveal and .reveal-fast
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Udfyld venligst navn og email");
      return;
    }
    setLoading(true);
    try {
      // Save to DB and send email in parallel
      const dbPromise = axios.post(`${API}/contact`, form);
      const emailPromise = emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone || "(ikke angivet)",
          company: form.company || "(ikke angivet)",
          services: form.services.length ? form.services.join(", ") : "(ikke angivet)",
          message: form.message || "(ingen besked)",
          to_email: "ldmediaa1@gmail.com",
          reply_to: form.email,
        },
        EMAILJS_PUBLIC_KEY
      );

      const results = await Promise.allSettled([dbPromise, emailPromise]);
      const dbOk = results[0].status === "fulfilled";
      const emailOk = results[1].status === "fulfilled";

      if (dbOk || emailOk) {
        toast.success("Tak! Vi vender tilbage inden for 24 timer.");
        setDone(true);
        setForm({ name: "", company: "", email: "", phone: "", services: [], message: "" });
        if (!emailOk) console.warn("EmailJS fejl:", results[1].reason);
        if (!dbOk) console.warn("DB fejl:", results[0].reason);
      } else {
        throw new Error("Begge leverancer fejlede");
      }
    } catch (err) {
      toast.error("Noget gik galt. Prøv igen eller skriv til ldmediaa1@gmail.com");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="pt-4 pb-12 lg:pt-6 lg:pb-16 relative overflow-hidden">
      {/* Subtle continuous green wash that fades down — connects smoothly with PageHero glow */}
      <div
        className="absolute inset-x-0 top-0 h-full pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, hsla(75, 100%, 67%, 0.06) 0%, hsla(75, 100%, 67%, 0.03) 30%, transparent 70%)",
        }}
      />
      <div
        className="absolute bottom-0 left-1/4 w-[480px] h-[320px] rounded-full pointer-events-none"
        style={{ background: "hsla(75, 100%, 67%, 0.04)", filter: "blur(110px)" }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
        <div className="fade-in-mount grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.04] p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))]/20 grid place-items-center text-[hsl(var(--accent))] text-lg">
                  ⚡
                </div>
                <div>
                  <div className="font-semibold text-white">Book tid direkte i kalenderen</div>
                  <div className="text-xs text-white/55 mt-0.5">Vælg selv et tidspunkt der passer dig.</div>
                </div>
              </div>
              <button
                type="button"
                onClick={openCalendly}
                data-testid="calendly-cta-btn"
                className="btn-pop w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] font-semibold text-sm hover:shadow-[0_10px_30px_-8px_hsla(75,100%,67%,0.7)]"
              >
                Vælg tid i kalenderen →
              </button>
            </div>

            <div className="my-7 flex items-center gap-4 text-xs text-white/35 uppercase tracking-widest">
              <span className="flex-1 h-px bg-white/10" />
              eller skriv en besked
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <ul className="space-y-3 text-white/80">
              {[
                "Uforpligtende møde. ingen kontrakter",
                "Find dine 3 største vækstmuligheder",
                "Tal direkte med en specialist",
                "Vi vender tilbage inden for 24 timer",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm">
                  <span className="mt-1 h-2 w-2 rounded-full bg-[hsl(var(--accent))] flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>

            <div className="mt-10 space-y-3">
              <InfoBlock label="Email" value="ldmediaa1@gmail.com" />
              <InfoBlock label="Telefon" value="+45 91 64 92 29" />
            </div>
          </div>

          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="lg:col-span-7 rounded-3xl border border-white/10 bg-[hsl(var(--bg-card))] p-7 lg:p-10"
          >
            <h3 className="font-display text-2xl font-bold mb-1">Bliv kontaktet</h3>
            <p className="text-sm text-white/55">Vi svarer typisk samme dag.</p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <Field label="Navn *" value={form.name} onChange={(v) => update("name", v)} testId="contact-name" />
              <Field label="Firmanavn" value={form.company} onChange={(v) => update("company", v)} testId="contact-company" />
              <Field type="email" label="Email *" value={form.email} onChange={(v) => update("email", v)} testId="contact-email" />
              <Field label="Telefon" value={form.phone} onChange={(v) => update("phone", v)} testId="contact-phone" />
            </div>

            <div className="mt-6">
              <label className="block text-xs uppercase tracking-widest text-white/55 mb-3">
                Hvad kan vi hjælpe med?
              </label>
              <div className="flex flex-wrap gap-2">
                {ALL_SERVICES.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleService(s)}
                      data-testid={`service-pill-${s.replace(/\s/g, "-").toLowerCase()}`}
                      className={`btn-pop px-4 py-2 rounded-full border text-sm transition-colors ${
                        active
                          ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] border-[hsl(var(--accent))]"
                          : "border-white/15 text-white/75 hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">Besked</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Fortæl os om din virksomhed og hvad du gerne vil opnå..."
                className="w-full rounded-2xl bg-white/[0.02] border border-white/10 focus:border-[hsl(var(--accent))] focus:outline-none p-4 text-white placeholder-white/35 transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-pop mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] font-semibold disabled:opacity-60 hover:shadow-[0_12px_40px_-8px_hsla(75,100%,67%,0.7)]"
            >
              {loading ? "Sender..." : done ? "Tak. vi vender tilbage!" : "Send besked →"}
            </button>

            <p className="mt-5 text-xs text-white/40">
              Vi behandler dine oplysninger fortroligt og deler dem aldrig med tredjepart.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest text-white/55 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full rounded-xl bg-white/[0.02] border border-white/10 focus:border-[hsl(var(--accent))] focus:outline-none px-4 py-3 text-white transition-colors"
    />
  </div>
);

const InfoBlock = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 p-4 flex items-center justify-between gap-3">
    <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-semibold text-white text-sm sm:text-base break-all text-right">{value}</div>
  </div>
);
